/**
 * Copyright (c) 2026 Briq-n-Braq LLC
 * All rights reserved.
 *
 * Proprietary and confidential software.
 * Unauthorized copying, modification, distribution, or use
 * of this software, in whole or in part, is strictly prohibited.
 *
 * KNX Supply Portal — portal.briq-n-braq.com
 * KNX Certified Professional | Partner No. 20248
 */

import React, { useState } from 'react';

const C = {
  navy:        '#0f1f3d',
  navyMid:     '#162d52',
  navyLight:   '#1e3a6e',
  navyBorder:  '#253d6e',
  orange:      '#F97316',
  orangeDim:   '#c2591b',
  white:       '#ffffff',
  slate:       '#94a3b8',
  slateLight:  '#cbd5e1',
  slateLighter:'#e2e8f0',
};

const PHASES = [
  {
    id: 'planning',
    label: 'Planning & Design',
    num: '01',
    steps: [
      {
        id: 'p1s1',
        label: 'Site Survey & Requirements',
        note: 'Walk every zone with the client to inventory all loads: lighting circuits, HVAC zones, blind/shutter groups, and AV points. Confirm mains supply type (single/three-phase), distribution board location, and cable routing paths. Capture client automation goals and any future-proofing scope in writing.',
        checklist: [
          'Zone & load inventory',
          'Mains supply configuration',
          'Cable routing survey',
          'Client automation brief',
          'Future-proofing requirements',
        ],
      },
      {
        id: 'p1s2',
        label: 'Load Calculation',
        note: 'Calculate total connected load per circuit and the number of KNX devices per line. A standard KNX line supports up to 64 devices; bus power supply (SELV 29 V DC) must not exceed 640 mA per segment. Size each PSU with a 20% headroom margin. Account for in-rush current when specifying fuse ratings.',
        checklist: [
          'Devices per line count',
          'Bus current per segment (mA)',
          'PSU rating (160/320/640 mA)',
          '20% headroom margin applied',
          'Fuse ratings documented',
        ],
      },
      {
        id: 'p1s3',
        label: 'KNX TP Topology Design',
        note: 'Design a KNX TP tree/star/bus topology — ring topologies are not permitted. Each line holds up to 64 devices; up to 15 lines per area and 15 areas per installation (max ~57,375 devices). Place line couplers to segment lines and area couplers for backbone separation. Document all physical addresses (PA) in a PA map before installation begins.',
        checklist: [
          'Line/area structure mapped',
          'Star, tree, or bus topology only',
          'Line coupler placement confirmed',
          'Area coupler backbone design',
          'Physical address (PA) map created',
        ],
      },
      {
        id: 'p1s4',
        label: 'ETS6 Project Setup',
        note: 'Create the ETS6 project and define the building structure (building → floor → room). Import device product databases (.knxprod files from KNX Association catalogue or manufacturer). Set project language/locale and assign the project to the Briq-n-Braq KNX Partner account (No. 20248) for licence compliance. Enable auto-backup in ETS preferences.',
        checklist: [
          'ETS6 project created',
          'Building / floor / room structure',
          '.knxprod files imported',
          'Partner licence assigned (20248)',
          'Auto-backup enabled',
        ],
      },
      {
        id: 'p1s5',
        label: 'Group Address Strategy',
        note: 'Define whether to use a 2-level (31 main × 2,048 sub) or 3-level (31 × 8 × 256) GA structure. Establish a naming convention before assigning any GAs — e.g. "Floor / Zone / Function". Plan sending GAs and separate status feedback GAs for actuators. Reserve a dedicated GA block for visualisation and diagnostics.',
        checklist: [
          'GA structure selected (2 or 3-level)',
          'Naming convention documented',
          'Sending vs status GA separation',
          'Visualisation GA block reserved',
          'GA spreadsheet template prepared',
        ],
      },
    ],
  },
  {
    id: 'installation',
    label: 'Installation',
    num: '02',
    steps: [
      {
        id: 'p2s1',
        label: 'KNX TP Cable Routing',
        note: 'Use KNX-approved YCYM 2×2×0.8 mm (or equivalent) twisted-pair cable. Maximum segment length is 1,000 m, with a maximum of 700 m between any two devices. Maintain a minimum 4 mm separation from 230 V cables in shared conduit. Observe the manufacturer\'s minimum bend radius (≥8× cable OD). Label both ends of each cable run with the target PA before termination.',
        checklist: [
          'YCYM 2×2×0.8 mm cable used',
          'Max 1,000 m segment length',
          'Max 700 m device-to-device',
          '≥4 mm separation from mains',
          'Both-end PA labelling applied',
        ],
      },
      {
        id: 'p2s2',
        label: 'Bus Power Supply',
        note: 'Install SELV 29 V DC bus power supplies of the appropriate current rating (160, 320, or 640 mA). Fit a line choke at the PSU output on each sub-segment to prevent bus signals bleeding into the PSU. Protect each line with a correctly-rated fuse. When multiple PSUs share a line, ensure consistent polarity and separate them with chokes.',
        checklist: [
          'SELV 29 V DC PSU fitted',
          'Line choke at PSU output',
          'Correct fuse rating per line',
          'Consistent polarity across line',
          'Redundant PSU (if specified)',
        ],
      },
      {
        id: 'p2s3',
        label: 'Device Mounting',
        note: 'Mount bus coupling units (BCUs) and application modules on 35 mm DIN rail or flush-mount boxes per the manufacturer\'s specification. Allow a minimum of 17.5 mm spacing between heat-dissipating devices. Terminate KNX TP cable using colour-coded KNX bus terminals (red = +, black = −). Check all terminations are torque-correct per the device specification.',
        checklist: [
          'DIN rail or flush-mount per spec',
          '≥17.5 mm device spacing',
          'KNX bus terminals used',
          'Red (+) / black (−) polarity',
          'Torque-correct terminations',
        ],
      },
      {
        id: 'p2s4',
        label: 'Line & Area Couplers',
        note: 'Install KNX line couplers (LC) to create additional sub-lines and to filter telegrams between segments, reducing backbone load. Area couplers (AC) join the main line to the KNX backbone. Filter tables — programmed in ETS — control which group addresses route between lines. Correct filter table configuration is critical for fault isolation and system performance.',
        checklist: [
          'Line couplers installed & powered',
          'Area couplers (if multi-area)',
          'Filter table strategy agreed',
          'Backbone wiring complete',
          'Coupler PSU requirements met',
        ],
      },
      {
        id: 'p2s5',
        label: 'IP Gateway / Router',
        note: 'For remote access or visualisation, install a KNXnet/IP router or gateway. Choose routing mode (multicast, for backbone integration) or tunnelling mode (single-client access). Assign a static LAN IP address and document it in the project file. Confirm the LAN switch supports multicast if using routing mode. Verify firewall rules permit KNXnet/IP port 3671 UDP.',
        checklist: [
          'KNXnet/IP router or gateway fitted',
          'Routing vs tunnelling mode set',
          'Static LAN IP assigned',
          'Multicast on switch confirmed',
          'Port 3671 UDP firewall rule',
        ],
      },
    ],
  },
  {
    id: 'commissioning',
    label: 'Commissioning',
    num: '03',
    steps: [
      {
        id: 'p3s1',
        label: 'Individual Addressing',
        note: 'Connect ETS to the bus via USB coupler, KNXnet/IP interface, or RS-232 adapter. Use "Bus → Individual Address" in ETS to assign each device its physical address (PA) in area.line.device notation (e.g. 1.1.5). Programme one device at a time using the programming button/LED. Confirm each PA in the ETS topology view before proceeding to avoid duplicates.',
        checklist: [
          'ETS connected (USB / IP / RS-232)',
          'PA assigned per topology plan',
          'Programming button confirmed per device',
          'PA verified in ETS topology view',
          'No duplicate PAs on any line',
        ],
      },
      {
        id: 'p3s2',
        label: 'Application Download',
        note: 'Download the application program, parameters, and PA to each device via ETS "Download → All". This writes the application layer, individual address, and all configured parameters in a single operation. Use partial downloads (Parameters only, App only) for re-commissioning efficiency. Confirm each download with a green tick in the ETS device list and save the project.',
        checklist: [
          'Full download (PA + App + Params)',
          'Download success ✓ in ETS list',
          'Partial download used where appropriate',
          'Manufacturer firmware up to date',
          'Project saved after each download',
        ],
      },
      {
        id: 'p3s3',
        label: 'Group Address Linking',
        note: 'Link communication objects (COs) to group addresses in the ETS GA workspace. Sending COs write to a GA; receiving COs listen. Link actuator status feedback COs to separate status GAs so sensors and visualisations can display current state. Validate every link against the GA documentation spreadsheet. Resolve CO datatype mismatches before testing.',
        checklist: [
          'Sending COs linked to GAs',
          'Receiving COs linked correctly',
          'Status feedback GAs linked',
          'All links validated vs GA spreadsheet',
          'CO datatype conflicts resolved',
        ],
      },
      {
        id: 'p3s4',
        label: 'Scene & Logic Programming',
        note: 'Programme multi-channel scenes using scene actuators or a dedicated logic controller (e.g. MDT Logic Controller, Gira L1). Set up time schedules, astronomical clock functions, and conditional logic blocks. Trigger each scene via its GA and verify that all linked devices respond within expected latency (<200 ms under normal bus load). Document all logic diagrams in the project archive.',
        checklist: [
          'Scene GAs defined & tested',
          'Astronomical clock calibrated',
          'Time schedules verified',
          'Conditional logic blocks tested',
          'Scene recall latency <200 ms',
        ],
      },
      {
        id: 'p3s5',
        label: 'Bus Load & Function Test',
        note: 'Open ETS Diagnostics and monitor telegram frequency per line. Typical residential bus load should remain below 25% of line capacity (≈1,000 telegrams/min on TP). Investigate any ACK timeout, address conflict, or repetitive error telegrams and resolve before handover. Perform a systematic room-by-room function test and record results against the client requirements sign-off sheet.',
        checklist: [
          'Bus load <25% per line',
          'No ACK timeout errors',
          'No address conflicts detected',
          'Room-by-room function test done',
          'Results recorded on sign-off sheet',
        ],
      },
    ],
  },
  {
    id: 'handover',
    label: 'Handover',
    num: '04',
    steps: [
      {
        id: 'p4s1',
        label: 'Final System Test',
        note: 'Conduct a comprehensive end-to-end test with the client present in each zone. Test all scenes, time schedules, override functions, and alarm/notification triggers. Verify failsafe behaviour (e.g. blinds lower on bus voltage loss). Check all visualisation panels and remote-access routes. Capture snag items on a formal snag list, resolve them, and obtain written client acceptance.',
        checklist: [
          'All zones tested with client present',
          'Scenes & schedules verified',
          'Failsafe behaviour confirmed',
          'Visualisation & remote access checked',
          'Snag list completed & resolved',
        ],
      },
      {
        id: 'p4s2',
        label: 'ETS Project Archive',
        note: 'Export the final ETS project as a .knxproj file. Store copies in at least two locations — client USB/handoff drive and secure cloud or company server. The archive must include: PA map, GA spreadsheet, as-built topology diagram, and device firmware version record. Label all distribution board devices with their PA for ease of future maintenance.',
        checklist: [
          '.knxproj file exported',
          'PA map & GA spreadsheet included',
          'As-built topology diagram',
          'Firmware versions recorded',
          'Distribution board PA labels fitted',
        ],
      },
      {
        id: 'p4s3',
        label: 'Client Training',
        note: 'Provide a structured walkthrough covering: basic device operation (switching, dimming, blind control), scene activation and recall, schedule viewing and adjustment, and app/visualisation use. Issue a written quick-start guide tailored to the installation. Demonstrate the procedure for reporting faults and arranging call-outs through Briq-n-Braq support.',
        checklist: [
          'Basic operation demonstrated',
          'Scene & schedule walkthrough',
          'App / visualisation training',
          'Quick-start guide issued',
          'Briq-n-Braq support contact given',
        ],
      },
      {
        id: 'p4s4',
        label: 'Documentation & Warranty',
        note: 'Issue the complete documentation package: as-built drawings, device data sheets, CE/KNX conformance certificates, warranty certificates, and the agreed maintenance schedule. Register KNX devices with manufacturers where applicable to activate extended warranty. Recommend an annual health check to verify bus integrity, apply firmware updates, and re-calibrate astronomical clock drift.',
        checklist: [
          'As-built drawings issued',
          'Device data sheets provided',
          'Warranty certificates issued',
          'Manufacturer device registrations done',
          'Annual maintenance schedule agreed',
        ],
      },
    ],
  },
];

function StepArrow() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', margin: '3px 0', pointerEvents: 'none' }}>
      <svg width="12" height="16" viewBox="0 0 12 16" fill="none">
        <line x1="6" y1="0" x2="6" y2="9" stroke="#334155" strokeWidth="1.5" />
        <polygon points="6,16 1.5,8.5 10.5,8.5" fill="#334155" />
      </svg>
    </div>
  );
}

function PhaseConnector() {
  return (
    <div style={{
      flexShrink: 0,
      width: 36,
      paddingTop: 18,
      display: 'flex',
      justifyContent: 'center',
      alignSelf: 'flex-start',
    }}>
      <svg width="36" height="16" viewBox="0 0 36 16" fill="none">
        <line x1="0" y1="8" x2="25" y2="8" stroke={C.orange} strokeWidth="1.5" />
        <polygon points="36,8 25,3.5 25,12.5" fill={C.orange} />
      </svg>
    </div>
  );
}

function StepCard({ step, index, isSelected, onClick }) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '100%',
        background: isSelected
          ? `linear-gradient(135deg, ${C.orange} 0%, ${C.orangeDim} 100%)`
          : hovered ? C.navyLight : C.navyMid,
        border: `1.5px solid ${isSelected ? C.orange : hovered ? C.orange + '66' : C.navyBorder}`,
        borderRadius: 8,
        padding: '9px 12px',
        cursor: 'pointer',
        textAlign: 'left',
        color: C.white,
        transition: 'background 0.12s ease, border-color 0.12s ease',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 9,
        boxSizing: 'border-box',
        boxShadow: isSelected ? `0 0 0 2px ${C.orange}44` : 'none',
      }}
    >
      <span style={{
        width: 21,
        height: 21,
        borderRadius: 5,
        background: isSelected ? 'rgba(255,255,255,0.22)' : `${C.orange}22`,
        color: isSelected ? C.white : C.orange,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 10,
        fontWeight: 700,
        flexShrink: 0,
        border: `1px solid ${isSelected ? 'rgba(255,255,255,0.35)' : C.orange + '55'}`,
        marginTop: 1,
      }}>
        {index + 1}
      </span>
      <span style={{ fontSize: 12.5, fontWeight: 500, lineHeight: 1.35, flex: 1, paddingTop: 2 }}>
        {step.label}
      </span>
      <span style={{
        fontSize: 9,
        color: isSelected ? 'rgba(255,255,255,0.65)' : C.slate,
        flexShrink: 0,
        paddingTop: 5,
        transition: 'color 0.12s ease',
      }}>
        {isSelected ? '▲' : '▼'}
      </span>
    </button>
  );
}

function DetailPanel({ step, phase, onClose }) {
  return (
    <div style={{
      margin: '1.25rem 1.5rem 0',
      background: C.navyMid,
      border: `2px solid ${C.orange}`,
      borderRadius: 12,
      padding: '1.25rem 1.5rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem', gap: 12 }}>
        <div>
          <div style={{ marginBottom: 5 }}>
            <span style={{
              background: C.orange,
              color: C.white,
              fontSize: 11,
              fontWeight: 700,
              padding: '2px 9px',
              borderRadius: 4,
              letterSpacing: '0.02em',
            }}>
              Phase {phase.num} · {phase.label}
            </span>
          </div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.white, lineHeight: 1.3 }}>
            {step.label}
          </h3>
        </div>
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            background: C.navyLight,
            border: `1px solid ${C.navyBorder}`,
            color: C.slateLight,
            borderRadius: 6,
            width: 28,
            height: 28,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 13,
            flexShrink: 0,
          }}
        >
          ✕
        </button>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 260px',
        gap: '1.25rem',
      }}>
        <div>
          <div style={{
            color: C.orange,
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.09em',
            marginBottom: 8,
          }}>
            Technical Notes
          </div>
          <p style={{ margin: 0, color: C.slateLight, lineHeight: 1.7, fontSize: 13.5 }}>
            {step.note}
          </p>
        </div>

        <div style={{
          background: `${C.navy}99`,
          borderRadius: 8,
          padding: '1rem',
          border: `1px solid ${C.navyBorder}`,
        }}>
          <div style={{
            color: C.orange,
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.09em',
            marginBottom: 10,
          }}>
            Checklist
          </div>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {step.checklist.map((item, i) => (
              <li
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  color: C.slateLighter,
                  fontSize: 12.5,
                  paddingBottom: i < step.checklist.length - 1 ? 7 : 0,
                  marginBottom: i < step.checklist.length - 1 ? 7 : 0,
                  borderBottom: i < step.checklist.length - 1 ? `1px solid ${C.navyBorder}` : 'none',
                }}
              >
                <span style={{
                  width: 15,
                  height: 15,
                  borderRadius: 3,
                  border: `1.5px solid ${C.orange}`,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: 1,
                }}>
                  <span style={{ color: C.orange, fontSize: 9, fontWeight: 700 }}>✓</span>
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function KNXFlowchart() {
  const [selected, setSelected] = useState(null);

  const allSteps   = PHASES.flatMap(p => p.steps);
  const selectedStep  = selected ? allSteps.find(s => s.id === selected) : null;
  const selectedPhase = selected ? PHASES.find(p => p.steps.some(s => s.id === selected)) : null;

  function toggleStep(id) {
    setSelected(prev => prev === id ? null : id);
  }

  return (
    <div style={{
      fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
      background: C.navy,
      minHeight: '100vh',
      color: C.white,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* ── Header ── */}
      <div style={{
        background: C.navyMid,
        borderBottom: `3px solid ${C.orange}`,
        padding: '0.875rem 1.75rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            background: C.orange,
            color: C.white,
            fontWeight: 800,
            fontSize: 17,
            padding: '4px 10px',
            borderRadius: 6,
            letterSpacing: '-0.5px',
            lineHeight: 1,
          }}>
            BB
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, letterSpacing: '-0.3px', lineHeight: 1.2 }}>
              Briq-n-Braq
            </div>
            <div style={{ color: C.slate, fontSize: 11, marginTop: 1 }}>
              KNX Installation Flowchart
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: C.orange, fontWeight: 600, fontSize: 12.5 }}>
            KNX Partner No. 20248
          </div>
          <div style={{ color: C.slate, fontSize: 11, marginTop: 2 }}>
            ETS6 · KNX TP · KNXnet/IP
          </div>
        </div>
      </div>

      {/* ── Page title ── */}
      <div style={{ padding: '1.25rem 1.75rem 0.75rem', textAlign: 'center', flexShrink: 0 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-0.4px' }}>
          KNX Home Installation Workflow
        </h1>
        <p style={{ margin: '0.35rem 0 0', color: C.slate, fontSize: 12.5 }}>
          Click any step to view technical notes and commissioning checklist
        </p>
      </div>

      {/* ── Flowchart ── */}
      <div style={{ padding: '0.75rem 1.5rem 0', overflowX: 'auto', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', minWidth: 900, gap: 0 }}>
          {PHASES.map((phase, pi) => (
            <React.Fragment key={phase.id}>
              {pi > 0 && <PhaseConnector />}

              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Phase header */}
                <div style={{
                  background: `linear-gradient(135deg, ${C.navyLight} 0%, ${C.navyMid} 100%)`,
                  border: `2px solid ${C.orange}`,
                  borderRadius: 10,
                  padding: '9px 12px',
                  marginBottom: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}>
                  <div style={{
                    background: C.orange,
                    color: C.white,
                    fontWeight: 800,
                    fontSize: 13,
                    width: 32,
                    height: 32,
                    borderRadius: 7,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    fontVariantNumeric: 'tabular-nums',
                    letterSpacing: '-0.5px',
                  }}>
                    {phase.num}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 12.5, lineHeight: 1.25 }}>
                      {phase.label}
                    </div>
                    <div style={{ color: C.slate, fontSize: 10.5, marginTop: 2 }}>
                      {phase.steps.length} steps
                    </div>
                  </div>
                </div>

                {/* Steps */}
                {phase.steps.map((step, si) => (
                  <React.Fragment key={step.id}>
                    {si > 0 && <StepArrow />}
                    <StepCard
                      step={step}
                      index={si}
                      isSelected={selected === step.id}
                      onClick={() => toggleStep(step.id)}
                    />
                  </React.Fragment>
                ))}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ── Detail panel ── */}
      {selectedStep && (
        <DetailPanel
          step={selectedStep}
          phase={selectedPhase}
          onClose={() => setSelected(null)}
        />
      )}

      {/* ── Footer ── */}
      <div style={{
        margin: '2rem 1.5rem 0',
        padding: '0.875rem 1.25rem',
        background: C.navyMid,
        borderTop: `2px solid ${C.navyBorder}`,
        borderRadius: '8px 8px 0 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: 11.5,
        color: C.slate,
        flexShrink: 0,
        flexWrap: 'wrap',
        gap: 8,
      }}>
        <div>
          <span style={{ color: C.white, fontWeight: 600 }}>Briq-n-Braq</span>
          {' · '}KNX Certified Partner No.{' '}
          <span style={{ color: C.orange, fontWeight: 600 }}>20248</span>
        </div>
        <div>ETS6 · KNX TP · KNXnet/IP · IEC 14543-3</div>
      </div>
    </div>
  );
}
